import { useState, useMemo } from 'react';
import { Box, Button, Dialog, TextField } from '@cere/cere-design-system';
import { useVaultSession } from '@/src/state/session';

const NAME_PATTERN = /^[a-z0-9/_-]+$/;

function deriveDisplayName(name: string): string {
  const last = name.split('/').filter(Boolean).pop() ?? '';
  if (!last) return '';
  return last.charAt(0).toUpperCase() + last.slice(1).replace(/[-_]+/g, ' ');
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CreateScopeDialog({ open, onClose }: Props) {
  const session = useVaultSession();
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [displayTouched, setDisplayTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = useMemo(
    () => NAME_PATTERN.test(name) && name.length >= 2 && name.length <= 64,
    [name],
  );

  const effectiveDisplayName = displayTouched ? displayName : deriveDisplayName(name);

  async function handleSubmit() {
    if (!valid) return;
    setSubmitting(true);
    setError(null);
    try {
      await (await session.signingHandle()).scopes.create({
        name,
        displayName: effectiveDisplayName,
      });
      await session.refreshScopes();
      onClose();
      setName('');
      setDisplayName('');
      setDisplayTouched(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      title="Create scope"
      onClose={() => !submitting && onClose()}
      isLoading={submitting}
      customActions={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!valid || submitting}>
            {submitting ? 'Creating...' : 'Create'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText="Lowercase letters, digits, /, _, - only. Cannot change later."
          fullWidth
        />
        <TextField
          label="Display name"
          value={effectiveDisplayName}
          onChange={(e) => { setDisplayName(e.target.value); setDisplayTouched(true); }}
          fullWidth
        />
        {error && <Box sx={{ color: 'error.main' }}>{error}</Box>}
      </Box>
    </Dialog>
  );
}
